import { useQuery } from '@tanstack/react-query'
import { GroupBase, OptionsOrGroups } from 'react-select'

import { useGeneralEnquiryServiceArea } from '@/hooks/useGeneralEnquiryServiceArea'
import { useServiceAreas } from '@/hooks/useServiceAreas'
import { QUERY_KEYS } from '@/lib/queryKeys'
import companyService from '@/lib/services/companyService'
import { HydraMember } from '@/types/api'
import { CompanyEntity, ServiceAreaEntity } from '@/types/entities'

export interface ReferralServiceAreaOption {
  label: string
  value: ReferralServiceAreaOptionItem
  isNew: boolean
}

export type ReferralServiceAreaOptionItem = HydraMember<
  Pick<ServiceAreaEntity, 'id' | 'name' | 'isFcaServiceArea' | 'isNew'>
>

interface UseReferralServiceAreaOptionsResult {
  isLoading: boolean
  serviceAreaOptions: ReferralServiceAreaOption[]
  existingAndNewServicesOptions: OptionsOrGroups<
    ReferralServiceAreaOption,
    GroupBase<ReferralServiceAreaOption>
  >
}

export function useReferralServiceAreaOptions({
  company,
  includeGeneralEnquiry,
}: {
  company?: Pick<CompanyEntity, 'id'> | undefined

  // If set to `true`, the General Enquiry service will be included in the result
  // even if the specified `company` doesn't have that service.
  includeGeneralEnquiry?: boolean
}): UseReferralServiceAreaOptionsResult {
  const companyId = company?.id
  const allServiceAreas = useServiceAreas()
  const availableServiceAreasQuery = useQuery({
    queryKey: QUERY_KEYS.companyAvailableServiceAreas(companyId || 0),
    queryFn: () => companyService.getAvailableServiceAreas(companyId || 0),
    staleTime: 30000,
    enabled: !!companyId, // Only fetch this query if a company ID is provided
  })

  const generalEnquiryServiceArea = useGeneralEnquiryServiceArea()

  return companyId ? getCompanyServiceAreaOptions() : getAllServiceAreaOptions()

  function getCompanyServiceAreaOptions() {
    const availableServiceAreas =
      availableServiceAreasQuery.data?.['hydra:member'] || []

    if (!availableServiceAreas.length) {
      return {
        isLoading: true,
        serviceAreaOptions: [],
        existingAndNewServicesOptions: [],
      }
    }

    let availableServiceAreasOptions = availableServiceAreas.map(
      (serviceArea): ReferralServiceAreaOption => ({
        label: serviceArea.name,
        value: serviceArea,
        isNew: serviceArea.isNew,
      }),
    )

    const companyHasGeneralEnquiryServiceArea = availableServiceAreas.some(
      (serviceArea) => serviceArea.id === generalEnquiryServiceArea.id,
    )

    if (includeGeneralEnquiry && !companyHasGeneralEnquiryServiceArea) {
      availableServiceAreasOptions.unshift({
        label: generalEnquiryServiceArea.name,
        value: generalEnquiryServiceArea,
        isNew: true,
      })
    }

    availableServiceAreasOptions = availableServiceAreasOptions.sort((a, b) =>
      a.label.localeCompare(b.label),
    )

    return {
      isLoading: false,
      serviceAreaOptions: availableServiceAreasOptions,
      existingAndNewServicesOptions: [
        {
          label: 'Services',
          options: availableServiceAreasOptions.filter(
            (serviceArea) => !serviceArea.isNew,
          ),
        },
        {
          label: 'New Services',
          options: availableServiceAreasOptions.filter(
            (serviceArea) => serviceArea.isNew,
          ),
        },
      ],
    }
  }

  function getAllServiceAreaOptions() {
    const allServiceAreaOptions = allServiceAreas.map(
      (serviceArea): ReferralServiceAreaOption => ({
        label: serviceArea.name,
        value: serviceArea,
        isNew: true,
      }),
    )

    return {
      isLoading: false,
      serviceAreaOptions: allServiceAreaOptions,
      existingAndNewServicesOptions: [
        {
          label: 'Services',
          options: allServiceAreaOptions,
        },
      ],
    }
  }
}
